// src/data/legacyBlacklist.js
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";

// ✅ Shared global collection name
const COLLECTION = "legacyblacklist-collection";

/**
 * Normalizes the formula record key consistently across app.
 * Used to compare and store blacklist entries.
 */
export function getCardKeyFromFields(record) {
  if (!record) return "";
  const str =
    record.raw ||
    record.color ||
    (record.name + (record.info ? JSON.stringify(record.info) : "")) ||
    "";
  return str.trim().toUpperCase();
}

/**
 * Fetches all blacklisted formulas (global for all users)
 * Returns a Set of normalized color field strings.
 */
export async function getLegacyBlacklistSet() {
  try {
    const snapshot = await getDocs(collection(db, COLLECTION));
    const set = new Set();
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (typeof data.color === "string") {
        set.add(data.color.trim().toUpperCase());
      }
    });
    return set;
  } catch (err) {
    console.error("❌ Error loading legacy blacklist:", err);
    return new Set();
  }
}

/**
 * Adds a formula to the global blacklist collection.
 * All users will see this update.
 * @param {string|object} colorFieldOrRecord - Either the color string or the record itself.
 * @param {string} [docName] - Optional document name (e.g. formula name)
 */
export async function addToLegacyBlacklist(colorFieldOrRecord, docName) {
  if (!colorFieldOrRecord) return null;

  // normalize the input into the same string key
  const colorValue =
    typeof colorFieldOrRecord === "string"
      ? colorFieldOrRecord.trim().toUpperCase()
      : getCardKeyFromFields(colorFieldOrRecord);

  const nameKey = docName?.trim() || colorValue.slice(0, 60); // fallback ID

  try {
    const ref = doc(db, COLLECTION, nameKey);
    await setDoc(ref, { color: colorValue });
    console.log(`✅ Blacklisted: ${nameKey}`);
    return ref.id;
  } catch (err) {
    console.error("❌ Error adding to legacy blacklist:", err);
    return null;
  }
}

/**
 * Removes a formula from the global blacklist.
 * @param {string} docId - Firestore document ID (usually the formula name)
 */
export async function removeFromLegacyBlacklist(docId) {
  if (!docId) return;
  try {
    const ref = doc(db, COLLECTION, docId);
    await deleteDoc(ref);
    console.log(`🗑️ Removed from legacy blacklist: ${docId}`);
  } catch (err) {
    console.error("❌ Error removing from legacy blacklist:", err);
  }
}

/**
 * Finds the blacklist document ID for a given color string.
 * Useful when un-blacklisting.
 * @param {string} colorField
 * @returns {Promise<string|null>}
 */
export async function findBlacklistDocId(colorField) {
  if (!colorField) return null;
  const q = query(
    collection(db, COLLECTION),
    where("color", "==", colorField.trim().toUpperCase())
  );
  try {
    const snap = await getDocs(q);
    let found = null;
    snap.forEach((docSnap) => {
      found = docSnap.id;
    });
    return found;
  } catch (err) {
    console.error("❌ Error finding blacklist doc ID:", err);
    return null;
  }
}
